import fp from 'lodash/fp';
import React, { useState, useCallback, useMemo, ComponentType } from 'react';
import { Common } from '@factorin/domain';

import { useTranslation } from 'Modules/core/i18n';
import { TabSliderSelector, cn } from 'Modules/ui';

import { Dropdown } from './Dropdown';
import { PaginationParametersControlType } from './types';
import styles from './index.module.scss';


export const PaginationParametersControl: ComponentType<PaginationParametersControlType> = ({
    options, value, onChange, className,
}) => {
    const valueLookup = useMemo(
        () => fp.map(fp.pipe(
            fp.concat([]),
            fp.findIndex(({ key }) => key === value),
        ), options),
        [options, value],
    );
    const tabSliderSelectorValue = useMemo(
        () => fp.findIndex(val => val !== -1, valueLookup),
        [valueLookup],
    );

    const [cache, setCache] = useState<Common.Catalog<number>>({});

    const getCached = useCallback(
        (idx: number) => valueLookup[idx] !== -1
            ? valueLookup[idx]
            : cache[idx] || 0,
        [cache, valueLookup],
    );
    const onTabChange = useCallback(
        (idx: number) => {
            if (idx === tabSliderSelectorValue)
                return;
            onChange(fp.isArray(options[idx])
                ? options[idx][getCached(idx)]
                : options[idx]);
        },
        [options, tabSliderSelectorValue, onChange, getCached],
    );
    const onChangeDropdownValue = useCallback(
        (idx: number) => (dropdownIdx: number) => {
            onChange(options[idx][dropdownIdx]);
            setCache({ ...cache, [idx]: dropdownIdx });
        },
        [options, cache, onChange],
    );

    const { t } = useTranslation('filters');

    const selectorOptions = useMemo(
        () => options.map<{ tab: React.ReactNode, clickable: boolean }>((option, idx) => ({
            tab: fp.isArray(option)
                ? (
                    <Dropdown
                        idx={idx}
                        options={option}
                        disabled={idx !== tabSliderSelectorValue}
                        onClick={onTabChange}
                        getValue={getCached}
                        onChange={onChangeDropdownValue}
                    />
                )
                : t(option.key),
            clickable: !fp.isArray(option),
        })),
        [options, tabSliderSelectorValue, getCached, onChangeDropdownValue, onTabChange, t],
    );

    return (
        <TabSliderSelector
            classNames={{
                container: cn(styles.control, className),
                positioner: styles.positioner,
            }}
            value={tabSliderSelectorValue}
            onChange={onTabChange}
            options={selectorOptions}
        />
    );
};

export * from './types';
